import type { EvidenceTopic } from "./evidence";

/**
 * SERVICE DETAIL — the four service slugs that existed in config with no routes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GAP THIS CLOSES
 *
 * `SERVICES` in business.ts has carried a `slug` on every entry since the first
 * pass, and nothing was ever routed at it. Four service-intent URLs — the exact
 * terms a buyer types when they know what they want but not who does it —
 * pointed nowhere.
 *
 * These pages sit in the cannibalisation map between the brand-wide `/services`
 * index and the geo cluster: `/services` owns "drone services", each detail page
 * owns its own service term, and a location page owns "{service} {town}". No two
 * compete.
 *
 * `craft` is the field that makes them worth reading. Anyone can list a service.
 * Stating what actually separates a good version of it from a bad one — the
 * altitude, the light, the move, the failure mode — is the thing a competitor's
 * page never contains, and the thing an answer engine has nothing else to cite.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface ServiceDetail {
  slug: string;
  name: string;
  h1: string;
  description: string;
  lede: string;
  /** 40–60 word quotable direct answer. */
  quickAnswer: string;
  /** What separates a good version from a bad one. The reason to read. */
  craft: { point: string; detail: string }[];
  /** Concrete deliverables for this service specifically. */
  deliverables: string[];
  /** What it is commonly commissioned for. */
  usedFor: string[];
  evidenceTopics: EvidenceTopic[];
  faqs: { question: string; answer: string }[];
  related: string[];
}

export const SERVICE_DETAIL: ServiceDetail[] = [
  {
    slug: "aerial-photography",
    name: "Aerial photography",
    h1: "Altitude is not a camera position. It is an argument.",
    description:
      "Aerial photography across Alberta and BC — property, land, site and campaign stills. Advanced RPAS certified, airspace authorisation filed where required, delivered colour-managed.",
    lede: "Every property photograph taken from the ground makes the same implicit claim: that what you can see is what matters. A plan view makes a different one — that the thing being sold is a relationship between a building, a parcel and everything around it.",
    quickAnswer:
      "Yes. Fly4MEdia shoots aerial stills across Alberta and British Columbia for property, land, construction and commercial subjects. Flights are conducted under a Transport Canada Advanced RPAS certificate, with NAV CANADA authorisation filed for any address inside controlled airspace — which covers most of urban Calgary.",
    craft: [
      {
        point: "Altitude is chosen from the subject, not the airframe",
        detail:
          "A five-acre parcel needs enough height to show the boundary and not so much that the house becomes a roof. Too high is the most common failure in amateur aerial work, and it is the one that makes every property look identical.",
      },
      {
        point: "The light decides the day, not the calendar",
        detail:
          "Low raking light separates terrain, grade and texture. Overhead midday sun flattens all three. A west-facing subject in the foothills has roughly one usable hour, and booking outside it wastes the flight.",
      },
      {
        point: "The frame includes what the buyer is actually paying for",
        detail:
          "Mountain sightline, water frontage, road access, acreage. Those are the priced features, and none of them exist in a photograph taken from the driveway.",
      },
      {
        point: "Colour is managed, not filtered",
        detail:
          "Consistent treatment across a set matters more than any single frame, particularly across a portfolio or a repeat programme where images will be compared.",
      },
    ],
    deliverables: [
      "High-resolution stills, colour-managed",
      "Web-optimised derivatives sized for listing portals",
      "Plan-view frames for boundary and access",
      "Raw files where the engagement calls for them",
    ],
    usedFor: [
      "Property listings where lot, approach or sightline carries the value",
      "Land and acreage assessment",
      "Site and facility documentation",
      "Campaign and brand imagery",
    ],
    evidenceTopics: ["real-estate", "commercial", "due-diligence"],
    faqs: [
      {
        question: "How high does a drone fly for property photography?",
        answer:
          "It depends on the subject rather than the limit. A single home usually reads best well below the regulatory ceiling; a large parcel needs enough height to contain the boundary. Flying as high as legally possible is the most common mistake in amateur aerial work.",
      },
      {
        question: "What time of day is best for aerial property photography?",
        answer:
          "The hour before sunset for most subjects, because low raking light separates grade, texture and terrain. Overhead midday sun flattens all three. West-facing foothills properties have a particularly narrow window.",
      },
      {
        question: "Do you need a licence to take aerial photographs commercially in Canada?",
        answer:
          "Yes. Commercial operation requires a Transport Canada RPAS Pilot Certificate. Advanced Operations is the level needed to fly in controlled airspace or near bystanders, which covers most addresses in urban Alberta.",
      },
    ],
    related: ["aerial-cinematography", "real-estate-media", "fpv-drone-filming"],
  },

  {
    slug: "aerial-cinematography",
    name: "Aerial cinematography",
    h1: "A move is a sentence. Most aerial video is a shrug.",
    description:
      "Aerial cinematography across Alberta and BC — orbits, reveals, push-ins and tracking passes. Advanced RPAS certified, planned against airspace, terrain and light before the flight.",
    lede: "The difference between aerial footage and aerial cinematography is whether the camera move means something. An orbit says look at this from every side. A reveal says here is what you could not see. A drift with no intent says nothing at all, at four thousand dollars a minute.",
    quickAnswer:
      "Yes. Fly4MEdia shoots aerial cinematography across Alberta and British Columbia — orbits, reveals, push-ins and tracking passes for property, commercial and brand work. Every flight is planned against airspace, terrain and the light window before the shoot date is confirmed.",
    craft: [
      {
        point: "Every move carries an argument",
        detail:
          "A rising reveal from valley floor to bench height exists to show elevation. An orbit exists to establish that a property has no bad side. If a move does not make a point, it is footage rather than cinematography.",
      },
      {
        point: "Speed is set by the subject's scale",
        detail:
          "A move that reads correctly over a suburban lot is frantic over a mountain valley and glacial over an interior. Matching speed to scale is what makes a sequence feel composed rather than piloted.",
      },
      {
        point: "Terrain decides what is flyable",
        detail:
          "A valley funnels wind and shortens the light window. In the Bow Valley the usable hour closes well before the published sunset, because the peaks take the sun first.",
      },
      {
        point: "The edit is planned at the flight",
        detail:
          "Shooting coverage and finding the film later produces a lot of unusable altitude. The shot list is built from the cut, so what is flown is what is used.",
      },
    ],
    deliverables: [
      "Edited video in the format the end use requires",
      "Graded, colour-consistent across the sequence",
      "Individual clean moves for the client's own edit",
      "Raw files where the engagement calls for them",
    ],
    usedFor: [
      "Property and development marketing films",
      "Brand and campaign work",
      "Venue, resort and tourism content",
      "Facility and site overview films",
    ],
    evidenceTopics: ["engagement", "commercial", "tourism", "real-estate"],
    faqs: [
      {
        question: "What is the difference between aerial video and aerial cinematography?",
        answer:
          "Intent. Aerial video records what is there from above. Cinematography chooses a move that makes a point — a reveal that shows elevation, an orbit that proves a property has no bad side. The technical capability is identical; the shot list is not.",
      },
      {
        question: "How long does an aerial cinematography shoot take?",
        answer:
          "The flying is the short part. The constraint is the light window, which in mountain terrain can be under an hour, and the weather window, which in Alberta's chinook belt can close inside one. Scheduling absorbs both rather than assuming neither.",
      },
      {
        question: "Can you fly a drone cinematically inside a control zone?",
        answer:
          "Yes, under an Advanced RPAS certificate with NAV CANADA authorisation filed in advance. Most of Calgary requires it, which is why some operators quietly exclude the inner city and the northeast from their coverage.",
      },
    ],
    related: ["aerial-photography", "fpv-drone-filming", "real-estate-media"],
  },

  {
    slug: "real-estate-media",
    name: "Real estate media",
    h1: "A listing photograph has one job: make the right buyer drive out",
    description:
      "Aerial real estate media across Alberta and BC — listing packages showing lot, approach, light and neighbourhood context. Advanced RPAS certified, turnaround agreed before booking.",
    lede: "Interior photography sells the house. Aerial media sells the decision to come and see it — the lot, the approach, the street, the sightline, and whether the thing in the listing description actually exists.",
    quickAnswer:
      "Yes. Fly4MEdia produces aerial media for property listings across Alberta and British Columbia — stills and video showing lot, approach, sightline and neighbourhood context. Airspace authorisation is filed for any address inside a control zone, which covers most of urban Calgary.",
    craft: [
      {
        point: "The lot is the frame most listings are missing",
        detail:
          "Interior photography is well covered by every listing photographer. Where a listing usually fails is proving the parcel, the frontage and the approach — which is exactly what cannot be shot from inside.",
      },
      {
        point: "Context is a feature, not a backdrop",
        detail:
          "Proximity to a lake, a ridge, a golf course or a school is priced into the property. An aerial frame is the only one that shows the house and the reason for its price simultaneously.",
      },
      {
        point: "Season is part of the brief",
        detail:
          "A lakefront property under ice photographs as a house beside a grey field. A mountain sightline disappears into late-summer wildfire smoke. Booking around those is a commercial decision, not a scheduling one.",
      },
      {
        point: "Turnaround is agreed before the shoot",
        detail:
          "A listing has a launch date. Delivery timing is set at booking rather than estimated afterwards, because media that arrives after the listing goes live has already lost most of its value.",
      },
    ],
    deliverables: [
      "Aerial stills sized for listing portals",
      "Short-form listing video",
      "Plan-view frames showing lot and boundary",
      "Neighbourhood and approach context frames",
    ],
    usedFor: [
      "Residential listings where lot or sightline carries value",
      "Acreage and estate marketing",
      "New-build and development launches",
      "Lakefront and view properties",
    ],
    evidenceTopics: ["real-estate", "engagement"],
    faqs: [
      {
        question: "Is drone photography worth it for a real estate listing?",
        answer:
          "It depends entirely on whether the property's value is visible from the street. On a zero-lot-line infill it adds little. On acreage, lakefront, a view property or anything where the parcel is the asset, it is the only frame that shows what the buyer is paying for.",
      },
      {
        question: "How quickly can listing media be delivered?",
        answer:
          "Turnaround is agreed at booking against the listing launch date rather than estimated afterwards. The variables that move a shoot date are weather and, inside a control zone, airspace authorisation — both established before the date is confirmed.",
      },
      {
        question: "When should a lakefront property be photographed?",
        answer:
          "Between ice-off and freeze-up. Under ice the water reads as a flat grey field and the waterfront premium the listing is built on simply does not appear in the frame.",
      },
    ],
    related: ["aerial-photography", "aerial-cinematography", "fpv-drone-filming"],
  },

  {
    slug: "fpv-drone-filming",
    name: "FPV drone filming",
    h1: "One continuous take through a space a conventional airframe cannot enter",
    description:
      "FPV drone filming across Alberta and BC — single-take flythroughs and dynamic tracking a conventional airframe cannot fly. Advanced RPAS certified, planned shot by shot.",
    lede: "An FPV flythrough is the one aerial shot an audience cannot look away from, because it does something no other camera can: it moves through a space continuously, at speed, without a cut.",
    quickAnswer:
      "Yes. Fly4MEdia flies FPV work across Alberta and British Columbia — single-take flythroughs, dynamic tracking and interior-to-exterior transitions. FPV is planned shot by shot and flown under an Advanced RPAS certificate, with airspace authorisation filed where required.",
    craft: [
      {
        point: "The route is choreographed before anything flies",
        detail:
          "An FPV shot is a rehearsed line through a space. Improvising it produces footage that reads as a stunt rather than a sequence, and in a tight interior it produces a crash.",
      },
      {
        point: "It is a specialist airframe, not a mode",
        detail:
          "FPV uses different equipment, different piloting and different risk assessment from conventional aerial work. Operators who offer it as a checkbox on the same airframe are offering something else.",
      },
      {
        point: "Speed sells the shot; control keeps it usable",
        detail:
          "The appeal is momentum. The failure is momentum without composition — a fast move past a subject the viewer never got to see.",
      },
      {
        point: "Interior-to-exterior is where it earns its cost",
        detail:
          "The transition out of a doorway or window in one unbroken move is the shot that cannot be faked, cannot be cut around, and is the reason FPV gets commissioned at all.",
      },
    ],
    deliverables: [
      "Single-take flythrough sequences",
      "Graded and stabilised final cut",
      "Clean unedited takes for the client's own edit",
      "Raw files where the engagement calls for them",
    ],
    usedFor: [
      "Property and venue flythroughs",
      "Brand and campaign films needing a signature move",
      "Facility and interior walkthroughs",
      "Event and action coverage",
    ],
    evidenceTopics: ["engagement", "commercial", "tourism"],
    faqs: [
      {
        question: "What is an FPV drone and how is it different?",
        answer:
          "FPV means first-person view — the pilot flies from the aircraft's camera perspective rather than by line of sight. It uses a different airframe, different piloting and different risk assessment, which is why it produces moves a conventional aerial platform cannot fly.",
      },
      {
        question: "Can an FPV drone fly indoors?",
        answer:
          "Yes, and interior-to-exterior transitions in one unbroken move are where FPV earns its cost. Interior work is choreographed and rehearsed before anything flies, because there is no margin to improvise in a confined space.",
      },
      {
        question: "Is FPV filming more expensive than standard aerial work?",
        answer:
          "It is scoped differently. The flying time is short; the planning, rehearsal and risk assessment are not. Work is quoted against the shot list rather than by the hour, so the number reflects the sequence being produced.",
      },
    ],
    related: ["aerial-cinematography", "aerial-photography", "real-estate-media"],
  },
];

export function getServiceDetail(slug: string): ServiceDetail | undefined {
  return SERVICE_DETAIL.find((s) => s.slug === slug);
}
